/**
 * REP-04: attaching evidence to a project entry.
 *
 * A file, and only a file. The panel used to take a URL as well, and a link is a different thing
 * wearing the same row: the server fetches it, what it reads can change or vanish afterwards, and
 * "Remove" deletes our copy of a page that is not ours. Evidence for a week should be the bytes
 * the student had, so the box is gone. Links already attached stay on the record and are still
 * listed, because a submitted week is not rewritten (REP-07).
 *
 * The file never passes through the API. The client hashes it, asks for permission to write one
 * key, PUTs the bytes straight to object storage, and then asks the server to confirm — which is
 * when the server checks the checksum. Three steps rather than one, because the middle one is the
 * part that must not go through this application.
 *
 * The text is read when the week is submitted, not as each file arrives, so a version attached and
 * not yet submitted sits at `pending`. The badge for that state says "Uploaded": what has
 * happened, rather than what is about to. It used to read "Reading…" over a file nothing was
 * reading, which looked like a job that had hung, and then "Read when you submit", which answered
 * a question about the future for someone who wanted to know the file had arrived.
 *
 * The extraction state is shown rather than hidden. A PDF the server could not read is not the
 * same as a PDF with nothing in it, and the student is the person best placed to fix it.
 *
 * Attaching takes a while, and the three steps take it for different reasons: the browser hashes
 * the whole file before anything leaves it, the bytes then cross the network to object storage,
 * and the server verifies the checksum at the end. On the live stack a 25 MB file spends seconds
 * in each. The panel says which step it is on and, while the bytes are moving, how far — a wait
 * a person can see the shape of is a wait they will sit through, and one that names no stage is
 * the wait they reload the page in the middle of.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "@/api/client";
import { Badge } from "@/components/evidence/Badges";
import { openArtifact } from "@/features/report/queries";
import { putWithProgress, sha256, type Sending, type UploadGrant } from "@/lib/upload";

export type Attachment = {
  artifact_id: string;
  version_no: number;
  filename: string;
  byte_size: number;
  extraction_state: "pending" | "ok" | "failed" | "unsupported";
  extraction_note: string;
  truncated?: boolean;
  /** Where a linked artifact points. A file has none, and that is how the two are told apart. */
  source_url?: string | null;
  /** What the student said this shows. Recorded on attach, and shown back on the row. */
  supported_claim?: string;
};

export function Attachments({
  projectId,
  periodId,
  attachments,
  onAttached,
}: {
  projectId: string;
  periodId: string;
  attachments: Attachment[];
  /** Called after anything is attached, so the caller can refetch the list from the server. */
  onAttached: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  // What is in flight, purely so the screen can say so. The file input cannot hold it: its value
  // is cleared on selection (see the change handler), and a disabled input showing nothing for the
  // several seconds this takes is indistinguishable from a broken one.
  const [sending, setSending] = useState<Sending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [claim, setClaim] = useState("");

  async function upload(file: File) {
    setBusy(true);
    // Hashing reads the whole file before a byte leaves the machine, and on a 25 MB file that is
    // a visible pause with nothing happening on screen. It gets a stage of its own.
    setSending({ name: file.name, stage: "checking", fraction: null });
    setError(null);
    try {
      const checksum = await sha256(file);
      const grant = await api.post<UploadGrant>("/api/v1/artifacts/uploads", {
        project_id: projectId,
        period_id: periodId,
        filename: file.name,
        byte_size: file.size,
        sha256: checksum,
        supported_claim: claim,
      });

      setSending({ name: file.name, stage: "sending", fraction: 0 });
      await putWithProgress(grant.url, grant.headers, file, (fraction) =>
        setSending({ name: file.name, stage: "sending", fraction }),
      );

      // The bytes have landed; the server now verifies the checksum against them. It is the step
      // that took 4.6s on the live stack, and it is not the upload — saying so is the difference
      // between a wait that looks stuck and a wait that looks like work.
      setSending({ name: file.name, stage: "recording", fraction: null });
      await api.post<Attachment>(`/api/v1/artifacts/${grant.artifact_id}/confirm`);
      onAttached();
      setClaim("");
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.problem.detail : String(problem));
    } finally {
      setBusy(false);
      setSending(null);
    }
  }

  /**
   * `name` identifies the thing to the person removing it, and `isLink` decides what is said
   * about it. Both used to be wrong for a link: the name came from `filename`, which is derived
   * from the URL's path — so every link without one was called "link" — and the warning said "the
   * file and its extracted text are deleted", which is not what happens. Nothing of the page at
   * the other end is deleted; it is not the student's to delete.
   */
  async function remove(artifactId: string, name: string, isLink: boolean) {
    const key = isLink
      ? "report.attachments.removeLinkConfirm"
      : "report.attachments.removeConfirm";
    if (!window.confirm(t(key, { name }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/api/v1/artifacts/${artifactId}`);
      onAttached();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.problem.detail : String(problem));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-7 border-t border-border pt-5" data-testid="attachments">
      <h3 className="section-title">{t("report.attachments.title")}</h3>
      <p className="stamp mt-1.5">{t("report.attachments.limit")}</p>

      <ul className="panel mt-3.5" data-testid="attachment-list">
        {attachments.map((attachment) => (
          <li
            key={`${attachment.artifact_id}:${attachment.version_no}`}
            className="row items-start"
          >
            <div className="min-w-0">
              {/* A link's filename is derived from its path, so two links to different sites can
                  both read "link". The address is the only thing that identifies one. */}
              {attachment.source_url ? (
                <a
                  href={attachment.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="link block break-all font-mono text-note"
                >
                  {attachment.source_url}
                </a>
              ) : (
                <span className="block font-mono text-note">{attachment.filename}</span>
              )}
              {/* The claim was written into a box labelled "What does this show?" and then never
                  shown again, so nobody could check it, correct it, or notice it was empty. */}
              {attachment.supported_claim ? (
                <p className="mt-0.5 text-note text-muted-foreground" data-testid="claim">
                  {attachment.supported_claim}
                </p>
              ) : null}
              {/* Why a file or link could not be read, as text. It was the badge's `title`, which
                  a phone has no way to show and a screen reader does not announce as content. */}
              {attachment.extraction_state === "failed" && attachment.extraction_note ? (
                <p className="mt-0.5 text-note text-bad" data-testid="extraction-note">
                  {attachment.extraction_note}
                </p>
              ) : null}
            </div>
            <span className="flex items-center gap-2.5">
              <ExtractionBadge attachment={attachment} />
              {/* A link has no stored bytes, so this asked for a download that 404s and threw
                  into the console with nothing on screen. The address above is the way to open
                  one, and it is already a link. */}
              {attachment.source_url ? null : (
                <button
                  type="button"
                  onClick={() => {
                    void openArtifact(attachment.artifact_id).catch((problem: unknown) =>
                      setError(
                        problem instanceof ApiError ? problem.problem.detail : String(problem),
                      ),
                    );
                  }}
                  className="btn-quiet"
                >
                  {t("report.attachments.download")}
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void remove(
                    attachment.artifact_id,
                    attachment.source_url ?? attachment.filename,
                    Boolean(attachment.source_url),
                  )
                }
                className="btn-quiet"
                data-testid="remove-attachment"
              >
                {t("report.attachments.remove")}
              </button>
            </span>
          </li>
        ))}
        {attachments.length === 0 && (
          <li className="px-4 py-2.5 text-sm text-muted-foreground">
            {t("report.attachments.none")}
          </li>
        )}
      </ul>

      <div className="mt-3.5 grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
        <label className="block">
          <span className="field-label">{t("report.attachments.claim")}</span>
          <input
            value={claim}
            onChange={(event) => setClaim(event.target.value)}
            placeholder={t("report.attachments.claimPlaceholder")}
            className="input placeholder:text-faint"
          />
          <span className="stamp mt-1.5 block">{t("report.attachments.claimNote")}</span>
        </label>

        <div className="flex flex-col gap-2.5">
          <label className="block">
            <span className="field-label">{t("report.attachments.file")}</span>
            <input
              type="file"
              disabled={busy}
              aria-busy={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
                // Cleared so that choosing the same file again still fires a change — after a
                // failure that is exactly what someone does. The name is shown below instead.
                event.target.value = "";
              }}
              className="file-input"
            />
          </label>
          {sending && (
            <div data-testid="attachment-sending">
              {/* `role="status"` rather than an alert: it is progress, not a problem, and it is
                  announced when it changes rather than interrupting whatever is being read. */}
              <p className="stamp" role="status">
                {t(`report.attachments.${sending.stage}`, { name: sending.name })}
                {sending.fraction === null
                  ? ""
                  : ` · ${t("report.attachments.percent", {
                      percent: Math.round(sending.fraction * 100),
                    })}`}
              </p>
              {/* Only drawn against a number something measured. An indeterminate bar here would
                  be decoration claiming to be information. */}
              {sending.fraction === null ? null : (
                <span
                  className="meter mt-1.5 block"
                  role="progressbar"
                  aria-label={t("report.attachments.sending", { name: sending.name })}
                  aria-valuenow={Math.round(sending.fraction * 100)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span
                    className="meter-fill motion-safe:transition-[width] motion-safe:duration-200"
                    style={{ width: `${Math.round(sending.fraction * 100)}%` }}
                  />
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-bad" data-testid="attachment-error">
          {error}
        </p>
      )}
    </section>
  );
}

/** A file we could not read and a file with nothing to read are different, and read differently. */
function ExtractionBadge({ attachment }: { attachment: Attachment }) {
  const { t } = useTranslation();
  const tone =
    attachment.extraction_state === "ok"
      ? "good"
      : attachment.extraction_state === "failed"
        ? "bad"
        : "neutral";
  return (
    <Badge tone={tone} title={attachment.extraction_note} data-testid="extraction-badge">
      {t(`report.attachments.state.${attachment.extraction_state}`)}
      {attachment.truncated ? ` · ${t("report.attachments.truncated")}` : ""}
    </Badge>
  );
}
